import React, { useState } from 'react';
import { PRESETS, AlertPreset, AlertCategory } from '../../hooks/useAlertRules';
import { Card } from '../ui/Card';
import { cn } from '../ui/Button';
import { ArrowRight, ArrowLeft, RefreshCw, TrendingUp, ArrowLeftRight, CloudSun, Zap, DollarSign } from 'lucide-react';
interface PresetPickerProps {
  onSelect: (preset: AlertPreset) => void;
  selectedId?: string;
}
const CATEGORY_INFO: {
  category: AlertCategory;
  description: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
}[] = [{
  category: 'Forecast update',
  description: 'Get notified when new forecast runs become available for your models and workspaces.',
  icon: RefreshCw,
  iconColor: 'text-white/70',
  iconBg: 'bg-white/[0.06]'
}, {
  category: 'Market signal',
  description: 'Monitor weather and price thresholds that impact your trading decisions.',
  icon: TrendingUp,
  iconColor: 'text-white/70',
  iconBg: 'bg-white/[0.06]'
}, {
  category: 'Model revision',
  description: 'Track significant changes between consecutive forecast runs.',
  icon: ArrowLeftRight,
  iconColor: 'text-white/70',
  iconBg: 'bg-white/[0.06]'
}];
type MarketSubCategory = 'Weather' | 'Power';
const MARKET_SUB_CATEGORIES: {
  key: MarketSubCategory;
  description: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
}[] = [{
  key: 'Weather',
  description: 'Wind speed, temperature, and other weather thresholds.',
  icon: CloudSun,
  iconColor: 'text-white/70',
  iconBg: 'bg-white/[0.06]'
}, {
  key: 'Power',
  description: 'Generation and capacity alerts for power market zones.',
  icon: Zap,
  iconColor: 'text-white/70',
  iconBg: 'bg-white/[0.06]'
}];
const MARKET_PRESET_MAP: Record<string, MarketSubCategory> = {
  'market-weather': 'Weather',
  'market-power': 'Power'
};
export function PresetPicker({
  onSelect,
  selectedId
}: PresetPickerProps) {
  const [selectedCategory, setSelectedCategory] = useState<AlertCategory | null>(null);
  const [marketSub, setMarketSub] = useState<MarketSubCategory | null>(null);
  // If a preset is already selected, auto-open its category
  const activeCategory = selectedCategory || (selectedId ? PRESETS.find(p => p.id === selectedId)?.category || null : null);
  if (!activeCategory) {
    // Step 1: Show category cards
    return <div className="space-y-4" data-id="element-565">
        <p className="text-sm text-slate-400" data-id="element-566">
          What kind of alert do you want to set up?
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-567">
          {CATEGORY_INFO.map(cat => {
          const Icon = cat.icon;
          return <button key={cat.category} onClick={() => {
            const catPresets = PRESETS.filter(p => p.category === cat.category);
            if (catPresets.length === 1 && cat.category !== 'Market signal') {
              onSelect(catPresets[0]);
              return;
            }
            setSelectedCategory(cat.category);
          }} className="group text-left outline-none focus:ring-2 focus:ring-pink-500/50 rounded-xl" data-id="element-568">
                <Card className="h-full p-5 !rounded-xl border-slate-700/50 bg-slate-800/40 hover:bg-slate-800/80 hover:border-slate-600 transition-all" data-id="element-569">
                  <div className="flex flex-col gap-3" data-id="element-570">
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border border-white/[0.08] backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]', cat.iconBg, cat.iconColor)} data-id="element-571">
                      <Icon size={20} data-id="element-572" />
                    </div>
                    <div data-id="element-573">
                      <h4 className="text-sm font-semibold text-slate-100 group-hover:text-white transition-colors mb-1" data-id="element-574">
                        {cat.category}
                      </h4>
                      <p className="text-[13px] text-slate-400 leading-relaxed" data-id="element-575">
                        {cat.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </button>;
        })}
        </div>
      </div>;
  }
  // Market signal sub-category step
  if (activeCategory === 'Market signal' && !marketSub) {
    const catInfo = CATEGORY_INFO.find(c => c.category === 'Market signal');
    return <div className="space-y-5" data-id="element-576">
        <button onClick={() => {
        setSelectedCategory(null);
        setMarketSub(null);
      }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors group" data-id="element-577">
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" data-id="element-578" />
          All categories
        </button>

        <div className="mb-2" data-id="element-579">
          <h3 className="text-base font-semibold text-white" data-id="element-580">Market signal</h3>
          <p className="text-xs text-slate-500" data-id="element-581">What type of market signal?</p>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-582">
          {MARKET_SUB_CATEGORIES.map(sub => {
          const subPresetCount = PRESETS.filter(p => p.category === 'Market signal' && MARKET_PRESET_MAP[p.id] === sub.key).length;
          const Icon = sub.icon;
          const comingSoon = subPresetCount === 0;
          return <button key={sub.key} onClick={() => {
            if (comingSoon) return;
            // Auto-select if sub-category has only 1 preset
            const subPresets = PRESETS.filter(p => p.category === 'Market signal' && MARKET_PRESET_MAP[p.id] === sub.key);
            if (subPresets.length === 1) {
              onSelect(subPresets[0]);
              return;
            }
            setMarketSub(sub.key);
          }} disabled={comingSoon} className={cn('group text-left outline-none focus:ring-2 focus:ring-pink-500/50 rounded-xl', comingSoon && 'opacity-50 cursor-not-allowed')} data-id="element-583">
                <Card className={cn('h-full p-5 !rounded-xl border-slate-700/50 bg-slate-800/40 transition-all', comingSoon ? 'border-slate-800/30' : 'hover:bg-slate-800/80 hover:border-slate-600')} data-id="element-584">
                  <div className="flex flex-col gap-3" data-id="element-585">
                    <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border border-white/[0.08] backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]', sub.iconBg, sub.iconColor)} data-id="element-586">
                      <Icon size={20} data-id="element-587" />
                    </div>
                    <div data-id="element-588">
                      <div className="flex items-center gap-2 mb-1" data-id="element-589">
                        <h4 className="text-sm font-semibold text-slate-100 group-hover:text-white transition-colors" data-id="element-590">
                          {sub.key}
                        </h4>
                        {comingSoon && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-600 border border-slate-700/50" data-id="element-591">
                            Coming soon
                          </span>}
                      </div>
                      <p className="text-[13px] text-slate-400 leading-relaxed" data-id="element-592">
                        {sub.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </button>;
        })}
        </div>
      </div>;
  }
  // Step 2/3: Show presets within selected category (filtered by sub-category for Market signal)
  const categoryPresets = PRESETS.filter(p => {
    if (p.category !== activeCategory) return false;
    if (activeCategory === 'Market signal' && marketSub) {
      return MARKET_PRESET_MAP[p.id] === marketSub;
    }
    return true;
  });
  // Auto-select if only one preset in this view
  if (categoryPresets.length === 1 && !selectedId) {
    // Use setTimeout to avoid calling onSelect during render
    setTimeout(() => onSelect(categoryPresets[0]), 0);
    return null;
  }
  const catInfo = CATEGORY_INFO.find(c => c.category === activeCategory);
  const subInfo = activeCategory === 'Market signal' && marketSub ? MARKET_SUB_CATEGORIES.find(s => s.key === marketSub) : null;
  return <div className="space-y-5" data-id="element-593">
      <button onClick={() => {
      if (activeCategory === 'Market signal' && marketSub) {
        setMarketSub(null);
      } else {
        setSelectedCategory(null);
        setMarketSub(null);
      }
    }} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors group" data-id="element-594">
        <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" data-id="element-595" />
        {activeCategory === 'Market signal' && marketSub ? 'Market signal' : 'All categories'}
      </button>

      <div className="mb-2" data-id="element-596">
        <h3 className="text-base font-semibold text-white" data-id="element-597">
          {subInfo ? `${activeCategory} — ${subInfo.key}` : activeCategory}
        </h3>
        <p className="text-xs text-slate-500" data-id="element-598">
          {subInfo?.description || catInfo?.description}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-599">
        {categoryPresets.map(preset => <button key={preset.id} onClick={() => onSelect(preset)} className={cn('group relative text-left transition-all duration-200 outline-none focus:ring-2 focus:ring-pink-500/50 rounded-xl', selectedId === preset.id ? 'ring-2 ring-pink-500 shadow-[0_0_20px_rgba(236,72,153,0.15)]' : 'hover:scale-[1.01]')} data-id="element-600">
            <Card className={cn('h-full p-5 !rounded-xl border-slate-700/50 bg-slate-800/40 hover:bg-slate-800/80 hover:border-slate-600 transition-all', selectedId === preset.id && 'bg-slate-800 border-pink-500/50')} data-id="element-601">
              <div className="flex items-start gap-4" data-id="element-602">
                <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]', preset.glassIconBg, preset.glassIconColor, selectedId === preset.id ? 'border-pink-500/30' : 'border-white/[0.08]')} data-id="element-603">
                  <preset.icon size={20} data-id="element-604" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-605">
                  <div className="flex items-center justify-between mb-1" data-id="element-606">
                    <h4 className="text-base font-semibold text-slate-100 group-hover:text-white transition-colors" data-id="element-607">
                      {preset.name}
                    </h4>
                    <ArrowRight size={16} className={cn('text-slate-500 transition-all', selectedId === preset.id ? 'text-pink-500 translate-x-1' : 'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0')} data-id="element-608" />
                  </div>
                  <p className="text-sm text-slate-400 mb-4 leading-relaxed" data-id="element-609">
                    {preset.description}
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-610">
                    {preset.required_inputs.map(input => <span key={input} className="inline-flex items-center px-2 py-1 rounded text-[10px] font-medium bg-slate-900/50 text-slate-500 border border-slate-700/50" data-id="element-611">
                        {input}
                      </span>)}
                  </div>
                </div>
              </div>
            </Card>
          </button>)}
      </div>
    </div>;
}